import _first from 'lodash/first'
import _split from 'lodash/split'
import _get from 'lodash/get'
import _has from 'lodash/has'
import _includes from 'lodash/includes'
import _isString from 'lodash/isString'
import _isNumber from 'lodash/isNumber'
import _parseInt from 'lodash/parseInt'
import _startsWith from 'lodash/startsWith'
import _toString from 'lodash/toString'
import _trim from 'lodash/trim'
import isNil from 'lodash/isNil'
import { get as getConfig } from '../../config/index'
import matchPath from '../resolver/match-path'
import { ROLE_VIEW_SITE } from '../auth-access/index'
import { ES, DE, FR } from '../languages/constants'
import { getUrl, TYPE_BROOKLYN } from '../../api-client/index'
import { UI_LOCATION } from '../event-tracking/index'
import {
  URL_CONTACT,
  URL_HELP_CENTER,
  URL_HELP_CENTER_ES,
  URL_HELP_CENTER_FR,
  URL_HELP_CENTER_DE,
  URL_EMAIL_CUSTOMER_SERVICE,
  URL_EMAIL_CUSTOMER_SERVICE_ES,
  URL_EMAIL_CUSTOMER_SERVICE_DE,
  URL_EMAIL_CUSTOMER_SERVICE_FR,
  URL_FAQ,
  URL_FAQ_ES,
  URL_WATCH_GAIA_ON_TV,
  URL_PLAN_SELECTION,
  URL_PLAN_SELECTION_PLANS,
  URL_ACCOUNT,
  URL_ACCOUNT_CREATION,
  URL_CART_ACCOUNT_CREATION_CREATE,
  URL_CART_BILLING,
  URL_CART_BILLING_PAYMENT,
  URL_CART_CONFIRMATION,
  URL_CART_ACCESS_DENIED,
  URL_ACCOUNT_PROFILE,
  URL_ACCOUNT_SETTINGS,
  URL_ACCOUNT_CANCEL,
  URL_ACCOUNT_CANCEL_CONFIRM,
  URL_ACCOUNT_CANCEL_OFFER,
  URL_ACCOUNT_PAUSE,
  URL_ACCOUNT_PAUSE_CONFIRM,
  URL_ACCOUNT_INVOICE,
  URL_ACCOUNT_UPDATE_PAYMENT,
  URL_AMBASSADORS,
  URL_REFER_JOIN,
  URL_REFER,
  URL_HOME,
  URL_LOGOUT,
  URL_LOGIN,
  URL_LIVE_PAGE,
  OPTIMIZELY_DISABLED,
  URL_EMAIL_SETTINGS,
  URL_GIFT,
  URL_GIFT_REVIEW,
  URL_GIFT_CREATE,
  URL_GIFT_PAYMENT,
  URL_GIFT_REDEEM,
  URL_GIFT_COMPLETE,
  URL_GIFT_TERMS_AND_CONDITIONS,
  URL_GIFT_TERMS_AND_CONDITIONS_ES,
  URL_GIFT_TERMS_AND_CONDITIONS_FR,
  URL_GIFT_TERMS_AND_CONDITIONS_DE,
  URL_TECHNICAL_PLAY_ISSUES_EN,
  URL_TECHNICAL_PLAYBACK_ISSUES_DE,
  URL_TECHNICAL_PLAYBACK_ISSUES_ES,
  URL_TECHNICAL_OFFLINE_CONTENT_DOWNLOAD_EN,
  URL_TECHNICAL_OFFLINE_CONTENT_DOWNLOAD_DE,
  URL_TECHNICAL_OFFLINE_CONTENT_DOWNLOAD_ES,
  URL_TECHNICAL_SETUP_CONTENT_TV_EN,
  URL_TECHNICAL_SETUP_CONTENT_TV_DE,
  URL_TECHNICAL_SETUP_CONTENT_TV_ES,
  URL_TECHNICAL_TROUBLE_FINDING_CONTENT_EN,
  URL_TECHNICAL_TROUBLE_FINDING_CONTENT_DE,
  URL_TECHNICAL_TROUBLE_FINDING_CONTENT_ES,
  URL_TECHNICAL_HELP_EN,
  URL_TECHNICAL_HELP_DE,
  URL_TECHNICAL_HELP_ES,
  URL_GAIA_PLUS,
  PARAM_LOGIN_ASSISTANCE_FORM_ID,
  URL_CANCEL_ACCOUNT_HELP,
  URL_CANCEL_ACCOUNT_HELP_ES,
  URL_CANCEL_ACCOUNT_HELP_FR,
  URL_CANCEL_ACCOUNT_HELP_DE,
  URL_YOGA,
  URL_HUBS_ANCIENT_WISDOM,
  URL_HUBS_PERSONAL_DEVELOPMENT,
  URL_HUBS_ALTERNATIVE_HEALTH,
  URL_GAIA_PLUS_ALL_GUIDES,
  DEFAULT_PATH_ORIGINAL,
  URL_MARKETPLACE_LOGGED_IN,
  URL_YOGA_SERIES,
  URL_YOGA_LIFESTYLE,
  URL_YOGA_TEACHERS,
  URL_MEDITATION_PRACTICES,
  URL_YOGA_PRACTICES,
  URL_RECENTLY_ADDED,
  URL_MARKETPLACE,
  URL_MARKETPLACE_LOGGED_OUT,
  URL_MARKETPLACE_LOGGED_OUT_TEST,
  URL_REMOTE_ACCOUNT_CREATE,
  URL_CHECKOUT,
  URL_LOGIN_WITH_LINK,
  URL_MANAGE_PROFILES,
  URL_AI_CHAT,
  URL_ABOUT,
  URL_GIFT_LP,
  URL_GIFT_LP_ES,
  URL_GIFT_LP_FR,
  URL_GIFT_LP_DE,
  URL_GET_STARTED,
  URL_AMA_EVENTS,
} from './constants'

const { origin, features } = getConfig()

const FILE_REGEX = /\.[a-z0-9/-]{0,4}$/i
const NOT_RELATIVE_REGEX = /^(https?:\/\/)/
const FULL_URL_REGEX = /^https?:\/\//
const ANCHOR_REGEX = /^#{1}[^#]+/
const ACCOUNT_REGEX = /^\/account/
const ACCOUNT_CANCEL_REGEX = /^\/account\/cancel/

export function isRelative (url) {
  return !NOT_RELATIVE_REGEX.test(url)
}

export function isFullUrl (url) {
  return FULL_URL_REGEX.test(url)
}

export function isAnchor (url) {
  return ANCHOR_REGEX.test(url)
}

export function createAbsolute (url) {
  const urlString = url || ''
  if (isRelative(urlString) && !isAnchor(urlString)) {
    const delimeter = urlString[0] === '/' ? '' : '/'
    return `${origin}${delimeter}${urlString}`
  }
  return urlString
}

export function createRelative (url) {
  const urlString = url || ''
  if (!isRelative(urlString)) {
    return urlString.replace(/https?:\/\/[^/]+/, '')
  }
  return (urlString[0] === '/' && urlString) || `/${urlString}`
}

export function createNextUrl (options) {
  const { totalPages, pathname, page } = options
  if (!_isNumber(totalPages) || (!_isNumber(page) && !_isString(page))) {
    return null
  }
  const pageNumber = _parseInt(page)
  if (pageNumber >= totalPages || pageNumber <= 0) {
    return null
  }
  return `${origin}${pathname}?page=${pageNumber + 1}`
}

export function createPrevUrl (options) {
  const { totalPages, pathname, page } = options
  if (!_isNumber(totalPages) || (!_isNumber(page) && !_isString(page))) {
    return null
  }
  const pageNumber = _parseInt(page)
  if (pageNumber <= 1 || pageNumber > totalPages) {
    return null
  }
  const queryString = pageNumber === 2 ? '' : `?page=${pageNumber - 1}`
  return `${origin}${pathname}${queryString}`
}

export function createCanonicalUrl ({ pathname, query }) {
  const queryPage = _get(query, 'page')
  const pageNumber = _isString(queryPage) || _isNumber(queryPage) ? _parseInt(queryPage) : null
  const correctedPathname = _toString(pathname).replace(/\/$/, '')
  return `${origin}${correctedPathname}${
    _isNumber(pageNumber) ? `?page=${pageNumber}` : ''
  }`
}

export function getGiftLpUrl (language) {
  switch (language) {
    case ES:
      return URL_GIFT_LP_ES
    case FR:
      return URL_GIFT_LP_FR
    case DE:
      return URL_GIFT_LP_DE
    default:
      return URL_GIFT_LP
  }
}

export function isGiftLp (pathname) {
  return (
    pathname === URL_GIFT_LP
    || pathname === URL_GIFT_LP_ES
    || pathname === URL_GIFT_LP_FR
    || pathname === URL_GIFT_LP_DE
  )
}

export function getGiftTermsAndConditionsUrl (language) {
  switch (language) {
    case ES:
      return URL_GIFT_TERMS_AND_CONDITIONS_ES
    case FR:
      return URL_GIFT_TERMS_AND_CONDITIONS_FR
    case DE:
      return URL_GIFT_TERMS_AND_CONDITIONS_DE
    default:
      return URL_GIFT_TERMS_AND_CONDITIONS
  }
}

export function getHelpCenterUrl (language) {
  switch (language) {
    case ES:
      return URL_HELP_CENTER_ES
    case FR:
      return URL_HELP_CENTER_FR
    case DE:
      return URL_HELP_CENTER_DE
    default:
      return URL_HELP_CENTER
  }
}

export function getTechnicalPlaybackIssuesUrl (language) {
  switch (language) {
    case DE:
      return URL_TECHNICAL_PLAYBACK_ISSUES_DE
    case ES:
      return URL_TECHNICAL_PLAYBACK_ISSUES_ES
    default:
      return URL_TECHNICAL_PLAY_ISSUES_EN
  }
}

export function getTechnicalOfflineDownloadUrl (language) {
  switch (language) {
    case DE:
      return URL_TECHNICAL_OFFLINE_CONTENT_DOWNLOAD_DE
    case ES:
      return URL_TECHNICAL_OFFLINE_CONTENT_DOWNLOAD_ES
    default:
      return URL_TECHNICAL_OFFLINE_CONTENT_DOWNLOAD_EN
  }
}

export function getTechnicalSetupUrl (language) {
  switch (language) {
    case DE:
      return URL_TECHNICAL_SETUP_CONTENT_TV_DE
    case ES:
      return URL_TECHNICAL_SETUP_CONTENT_TV_ES
    default:
      return URL_TECHNICAL_SETUP_CONTENT_TV_EN
  }
}

export function getTechnicalTroubleFindingContentUrl (language) {
  switch (language) {
    case DE:
      return URL_TECHNICAL_TROUBLE_FINDING_CONTENT_DE
    case ES:
      return URL_TECHNICAL_TROUBLE_FINDING_CONTENT_ES
    default:
      return URL_TECHNICAL_TROUBLE_FINDING_CONTENT_EN
  }
}

export function getTechnicalHelpUrl (language) {
  switch (language) {
    case DE:
      return URL_TECHNICAL_HELP_DE
    case ES:
      return URL_TECHNICAL_HELP_ES
    default:
      return URL_TECHNICAL_HELP_EN
  }
}

export function getEmailCustomerServiceUrl (language) {
  switch (language) {
    case DE:
      return URL_EMAIL_CUSTOMER_SERVICE_DE
    case ES:
      return URL_EMAIL_CUSTOMER_SERVICE_ES
    case FR:
      return URL_EMAIL_CUSTOMER_SERVICE_FR
    default:
      return URL_EMAIL_CUSTOMER_SERVICE
  }
}

export function getLoginAssistanceUrl (language) {
  const url = getEmailCustomerServiceUrl(language)
  return `${url}?${PARAM_LOGIN_ASSISTANCE_FORM_ID}`
}

export function getCancelAccountHelpUrl (language) {
  switch (language) {
    case DE:
      return URL_CANCEL_ACCOUNT_HELP_DE
    case ES:
      return URL_CANCEL_ACCOUNT_HELP_ES
    case FR:
      return URL_CANCEL_ACCOUNT_HELP_FR
    default:
      return URL_CANCEL_ACCOUNT_HELP
  }
}

export function getFaqUrl (language) {
  switch (language) {
    case ES:
      return URL_FAQ_ES
    default:
      return URL_FAQ
  }
}

export function isYoga (pathname) {
  const pattern = /^\/yoga$|^\/yoga\/|^\/level\/|^\/duration\/|^\/focus\/|^\/style\/|^\/teacher\//
  return pattern.test(pathname)
}

export function isFitness (pathname) {
  const pattern = /^\/yoga\/fitness-and-pilates$|^\/workout-level\/|^\/length\/|^\/collections\/|^\/workout-style\/|^\/instructor\//
  return pattern.test(pathname)
}

// Film filters are different than yoga and fitness because we do not expose the filter options
// under their own paths, e.g. film-subject/metaphysics does not exist
export function isFilm (pathname) {
  const enabled = _get(getConfig(), ['features', 'filmFilters', 'enabled'])
  if (!enabled) {
    return false
  }
  const pattern = /^\/films-docs$|^\/films-docs\//
  return pattern.test(pathname)
}

export function isRecipe (pathname) {
  const pattern = /^\/recipes$|^\/recipes\/|^\/recipe\/|^\/diet\//
  return pattern.test(pathname)
}

export function isOriginal (pathname) {
  const pattern = /^\/all-series\/series$|^\/people\/|^\/program\/|^\/topic\//
  return pattern.test(pathname)
}

export function isConscious (pathname) {
  const pattern = /^\/transformation\/expanded-consciousness$|^\/duration\/|^\/format\/|^\/topic\//
  return pattern.test(pathname)
}

export function isMeditation (pathname) {
  return _startsWith(pathname, '/meditation/')
}

export function isVideo (pathname) {
  return _startsWith(pathname, '/video/')
}

export function isPortal (pathname) {
  return _startsWith(pathname, '/portal/')
}

export function isSeries (pathname) {
  return (
    _startsWith(pathname, '/tv/')
    || _startsWith(pathname, '/show/')
    || _startsWith(pathname, '/series/')
  )
}

export function isCourse (pathname) {
  return _startsWith(pathname, '/course/')
}

export function isPerson (pathname) {
  return (
    _startsWith(pathname, '/person/')
  )
}

export function isVideoOrSeries (pathname) {
  return isVideo(pathname) || isSeries(pathname)
}

export function isAnonymousWordPressContent (to, isLoggedIn, proxyAnonymousContent) {
  const isHandledByWordPress = isMarketingLayer((!_startsWith(to, '/')) ? `/${to}` : to, isLoggedIn)
  if (proxyAnonymousContent && isHandledByWordPress && !isLoggedIn && (typeof (isLoggedIn) !== 'undefined')) {
    return true
  }
  return false
}

export function isShop (path) {
  return _startsWith(path, URL_MARKETPLACE_LOGGED_IN)
    || _startsWith(path, URL_MARKETPLACE)
    || _startsWith(path, URL_MARKETPLACE_LOGGED_OUT)
    || _startsWith(path, URL_MARKETPLACE_LOGGED_OUT_TEST)
    || _includes(path, URL_MARKETPLACE_LOGGED_IN)
}

export function isAnonymousWpabTestContent (location, to, isLoggedIn, proxyAnonymousContent) {
  const isWpab = _get(location, ['query', 'wpab']) === '1'
  return (isWpab && isAnonymousWordPressContent(to, isLoggedIn, proxyAnonymousContent))
}

export function isContentPreferences (pathname) {
  return (
    pathname === '/account/content-preferences'
    || pathname === '/account/settings'
  )
}

export function isPlaylist (pathname) {
  return pathname === '/playlist/default'
}

export function isPlaylistPage (pathname) {
  return _startsWith(pathname, '/playlist/')
}

export function isActivate (pathname) {
  return pathname === '/activate'
}

export function isGetStarted (pathname) {
  return pathname === '/get-started'
}

export function isPolicy (pathname) {
  return pathname === '/terms-privacy'
}

export function isPasswordReset (pathname) {
  return pathname === '/password-reset'
}

export function isSearch (pathname) {
  return pathname === '/search'
}

export function isTopics (pathname) {
  return pathname === '/topics'
}

export function isMemberHome (pathname, loggedIn) {
  return pathname === URL_HOME && loggedIn
}

export function isAnonymousHome (pathname, loggedIn) {
  return pathname === URL_HOME && !loggedIn
}

export function isGo (pathname) {
  return pathname === '/go'
}

export function isRemoteAccountCreate (pathname) {
  return pathname === URL_REMOTE_ACCOUNT_CREATE
}

export function isLoginWithLink (pathname) {
  return pathname === URL_LOGIN_WITH_LINK
}

export function isNotFound (pathname) {
  return pathname === '/not-found'
}

export function isLogin (pathname) {
  return pathname === URL_LOGIN || pathname === '/beta-sign-in'
}

export function isLogout (pathname) {
  return pathname === URL_LOGOUT
}

export function isPlans (pathname) {
  return (
    pathname === URL_PLAN_SELECTION || pathname === URL_PLAN_SELECTION_PLANS
  )
}

export function isReferJoin (pathname) {
  return pathname === URL_REFER_JOIN
}

export function isRefer (pathname) {
  return pathname === URL_REFER
}
export function isPlanSelectionPlans (pathname) {
  return pathname === URL_PLAN_SELECTION_PLANS
}

export function isLivePage (pathname) {
  return pathname === URL_LIVE_PAGE
}

export function isCart (pathname) {
  return (
    pathname === URL_ACCOUNT_CREATION
    || pathname === URL_CART_ACCOUNT_CREATION_CREATE
    || pathname === URL_CART_BILLING
    || pathname === URL_CART_BILLING_PAYMENT
    || pathname === URL_CART_CONFIRMATION
  )
}

export function isCartCreateAccount (pathname) {
  return pathname === URL_ACCOUNT_CREATION
}

export function isCartAccountCreationCreate (pathname) {
  return pathname === URL_CART_ACCOUNT_CREATION_CREATE
}

export function isCartBilling (pathname) {
  return pathname === URL_CART_BILLING
}

export function isCartBillingPayment (pathname) {
  return pathname === URL_CART_BILLING_PAYMENT
}

export function isCartConfirmation (pathname) {
  return pathname === URL_CART_CONFIRMATION
}

export function isCartAccessDenied (pathname) {
  return pathname === URL_CART_ACCESS_DENIED
}

export function isMyAccountContactUsPage (pathname) {
  return pathname === '/user/contact-us'
}

export function isMyAccount (pathname) {
  return ACCOUNT_REGEX.test(pathname)
}

export function isAccountCancelPath (pathname) {
  return ACCOUNT_CANCEL_REGEX.test(pathname)
}

export function isManageProfilesPage (pathname) {
  return pathname === URL_MANAGE_PROFILES
}

export function isNotificationsPage (pathname) {
  return pathname === '/notifications'
}

export function isAccountPage (pathname) {
  return _startsWith(pathname, '/account')
}

export function isMyAccountPage (pathname) {
  return pathname === '/account' || pathname === '/account-details'
}

export function isFreeMonth (pathname) {
  return pathname === '/account/free-month'
}

export function isAccountCancelConfirmPage (pathname) {
  return pathname === URL_ACCOUNT_CANCEL_CONFIRM
}

export function isAccountCancel (pathname) {
  return pathname === URL_ACCOUNT_CANCEL
}

export function isAccountCancelOfferPage (pathname) {
  return pathname === URL_ACCOUNT_CANCEL_OFFER
}

export function isAccountCancelFlow (pathname) {
  return (
    pathname === URL_ACCOUNT_CANCEL
    || pathname === URL_ACCOUNT_CANCEL_OFFER
    || pathname === URL_ACCOUNT_CANCEL_CONFIRM
  )
}

export function isAccountPauseFlow (pathname) {
  return (
    pathname === URL_ACCOUNT_PAUSE || pathname === URL_ACCOUNT_PAUSE_CONFIRM
  )
}

export function isAccountInvoice (pathname) {
  return pathname === URL_ACCOUNT_INVOICE
}

export function isAccountChangePlan (pathname) {
  return pathname === '/account/change-plan'
}

export function isAccountUpdatePayment (pathname) {
  return pathname === URL_ACCOUNT_UPDATE_PAYMENT
}

export function isEmailSettings (pathname) {
  return pathname === URL_EMAIL_SETTINGS
}

export function isRecentlyAdded (pathname) {
  return pathname === '/recently-added'
}

export function isWatchHistory (pathname) {
  return pathname === '/watch-history'
}

export function isFullPlayer (query) {
  return _has(query, 'fullplayer')
}

export function isEmbedded (query) {
  return _get(query, 'embed') === 'true'
}

export function isOptimizelyDisabled (query) {
  return _has(query, OPTIMIZELY_DISABLED)
}

export function isSeekingTruth (pathname) {
  return pathname === '/seeking-truth'
}

export function isTransformation (pathname) {
  return pathname === '/transformation'
}

export function isMyYoga (pathname) {
  return pathname === '/my-yoga'
}

export function isAccountLanguage (pathname) {
  return pathname === '/account/language'
}

export function isYogaPage (pathname) {
  return pathname === URL_YOGA
}

export function isYogaTeachersPage (pathname) {
  return pathname === URL_YOGA_TEACHERS
}

export function isWatchGaiaOnTV (url) {
  return url === URL_WATCH_GAIA_ON_TV
}

export function isAccount (pathname) {
  return pathname === URL_ACCOUNT || pathname === '/account-details'
}

export function isAccountSettings (pathname) {
  return pathname === URL_ACCOUNT_SETTINGS
}

export function isAccountProfile (pathname) {
  return pathname === URL_ACCOUNT_PROFILE
}

export function isContact (url) {
  return url === URL_CONTACT
}

export function isHelpCenter (url) {
  return url === URL_HELP_CENTER
}

export function isGiftTermsPage (pathname) {
  return !isNil(matchPath({ path: URL_GIFT_TERMS_AND_CONDITIONS, pathname }))
    || !isNil(matchPath({ path: URL_GIFT_TERMS_AND_CONDITIONS_ES, pathname }))
    || !isNil(matchPath({ path: URL_GIFT_TERMS_AND_CONDITIONS_FR, pathname }))
    || !isNil(matchPath({ path: URL_GIFT_TERMS_AND_CONDITIONS_DE, pathname }))
}

export function isGiftReviewPage (pathname) {
  return !isNil(matchPath({ path: URL_GIFT_REVIEW, pathname }))
}

export function isGiftPaymentPage (pathname) {
  return !isNil(matchPath({ path: URL_GIFT_PAYMENT, pathname }))
}

export function isGiftRedeemPage (pathname) {
  return _startsWith(pathname, URL_GIFT_REDEEM)
}

export function isGiftCompletePage (pathname) {
  return !isNil(matchPath({ path: URL_GIFT_COMPLETE, pathname }))
}

export function isGift (pathname) {
  return !isNil(matchPath({ path: URL_GIFT, pathname })
    || matchPath({ path: URL_GIFT_CREATE, pathname })
    || matchPath({ path: URL_GIFT_REVIEW, pathname })
    || matchPath({ path: URL_GIFT_PAYMENT, pathname })
    || matchPath({ path: URL_GIFT_COMPLETE, pathname }))
    || _startsWith(pathname, URL_GIFT_REDEEM)
}

export function isGiftGiverPage (pathname) {
  return !isNil(matchPath({ path: URL_GIFT, pathname })
    || matchPath({ path: URL_GIFT_CREATE, pathname })
    || matchPath({ path: URL_GIFT_REVIEW, pathname })
    || matchPath({ path: URL_GIFT_PAYMENT, pathname })
    || matchPath({ path: URL_GIFT_COMPLETE, pathname }))
      && !_startsWith(pathname, URL_GIFT_REDEEM)
}

export function isFast404 (pathname) {
  return (
    pathname === '/sites/default'
    || _startsWith(pathname, '/sites/default/')
    || FILE_REGEX.test(pathname)
  )
}

export function isLive (pathname) {
  return _startsWith(pathname, '/live')
}

export function isLiveChannelPage (pathname) {
  return pathname === '/gaia-now'
}

export function isEventsPage (pathname) {
  return pathname === `${URL_GAIA_PLUS}`
}

export function isAmaEventsPage (pathname) {
  return pathname === URL_AMA_EVENTS
}

export function isLiveEvents (pathname) {
  return _startsWith(pathname, `${URL_GAIA_PLUS}`)
}

export function isEventsDetailsPage (pathname) {
  return _startsWith(pathname, `${URL_GAIA_PLUS}`)
}

export function isSharePage (pathname) {
  return _startsWith(pathname, '/share/')
}

export function isWordPressSharePage (pathname) {
  return _includes(pathname, '/share/video/')
}

export function isTranscendence (pathname) {
  return pathname === '/series/transcendence'
}

export function isTranscendenceGuide (pathname) {
  return pathname === '/guide/transcendence-master-class'
}

export function isCheckoutFlowPage (pathname) {
  return (
    _startsWith(pathname, URL_PLAN_SELECTION) || _startsWith(pathname, '/cart')
  )
}

export function isMultiplePlaylistsPage (pathname) {
  return _startsWith(pathname, '/playlist/default/')
}

export function isAmbassadorsPage (pathname) {
  return pathname === URL_AMBASSADORS
}

// hubs pages
export function isHubsAlternativeHealth (pathname) {
  return pathname === URL_HUBS_ALTERNATIVE_HEALTH
}

export function isHubsAncientWisdom (pathname) {
  return pathname === URL_HUBS_ANCIENT_WISDOM
}

export function isHubsPersonalDevelopment (pathname) {
  return pathname === URL_HUBS_PERSONAL_DEVELOPMENT
}

export function isSinglePageCheckout (pathname) {
  return pathname === URL_CHECKOUT
}

export function isOraclePage (pathname) {
  return pathname === URL_AI_CHAT || _startsWith(pathname, `${URL_AI_CHAT}/`)
}

export function isAboutPage (pathname) {
  return pathname === URL_ABOUT
}

/**
 * Evaluates if both paths belong to the same page.
 * @param {string} currentPath
 * @param {string} comparedPath
 * @returns {boolean}
 */
export function isPartialPathAllowed (currentPath, comparedPath) {
  return (
    _startsWith(comparedPath, '/playlist/default')
    && isMultiplePlaylistsPage(currentPath)
  )
}

export function anonymousAllowed (path, roles) {
  const allowedPaths = [URL_HOME, '/password-reset']
  // eslint-disable-next-line no-param-reassign
  path = _first(path.split('?'))
  if (
    process.env.NODE_ENV !== 'production'
    || _includes(roles, ROLE_VIEW_SITE)
    || _includes(allowedPaths, path)
  ) {
    return true
  }
  return false
}

/**
 * Catch all for the marketing layer link handling
 */
export function isMarketingLayer (pathname, isLoggedIn) {
  return (
    isLandingPage(pathname)
    || isArticles(pathname)
    || isArticle(pathname)
    || (isSeries(pathname)
      && _get(features, ['marketing', 'proxyAnonymousSeries'])
      && !isLoggedIn)
    || (isPerson(pathname)
      && _get(features, ['marketing', 'proxyAnonymousPerson'])
      && !isLoggedIn)
    || (isVideo(pathname)
      && _get(features, ['marketing', 'proxyAnonymousVideo'])
      && !isLoggedIn)
    || isGiftTermsPage(pathname)
  )
}

export function isArticles (pathname) {
  return (
    pathname === '/articles'
    || pathname === '/articles/latest'
    || _includes(pathname, '/articles/list')
  )
}

export function isArticle (pathname) {
  return _startsWith(pathname, '/article/')
}

export function isLandingPage (pathname) {
  return (
    _startsWith(pathname, '/lp/')
    || _startsWith(pathname, '/es/lp/')
    || _startsWith(pathname, '/de/lp/')
    || _startsWith(pathname, '/fr/lp/')
  )
}

export function skipGettingStarted (path) {
  const skipUrls = [
    '/get-started',
    URL_PLAN_SELECTION,
    '/cart/account-creation',
    '/cart/billing',
    URL_CHECKOUT,
  ]
  return _includes(skipUrls, path)
}

export function getPathLocationName (pathname) {
  if (!_isString(pathname)) return UI_LOCATION.UNKNOWN_PAGE
  const pathsAreEqual = (path) => {
    return createAbsolute(_trim(pathname)) === createAbsolute(path)
  }

  if (pathsAreEqual(URL_HOME)) return UI_LOCATION.MEMBER_HOME
  if (pathsAreEqual(URL_MEDITATION_PRACTICES)) return UI_LOCATION.MEDITATION_PRACTICES
  if (pathsAreEqual(URL_YOGA)) return UI_LOCATION.YOGA_HOME
  if (pathsAreEqual(URL_YOGA_PRACTICES)) return UI_LOCATION.YOGA_PRACTICES
  if (pathsAreEqual(DEFAULT_PATH_ORIGINAL)) return UI_LOCATION.ALL_SERIES
  if (pathsAreEqual(URL_RECENTLY_ADDED)) return UI_LOCATION.RECENTLY_ADDED
  if (pathsAreEqual(URL_GAIA_PLUS_ALL_GUIDES)) return UI_LOCATION.GUIDES
  if (isOraclePage(pathname)) return UI_LOCATION.AI_CHAT
  if (pathsAreEqual(URL_ACCOUNT)) return UI_LOCATION.MY_ACCOUNT
  if (_startsWith(pathname, '/topic/')
    || pathsAreEqual(URL_YOGA_SERIES)
    || pathsAreEqual(URL_YOGA_LIFESTYLE)) return UI_LOCATION.TOPIC_PAGE
  if (isMeditation(pathname)) return UI_LOCATION.MEDITATION_PRACTICES
  if (isYoga(pathname)) return UI_LOCATION.YOGA_PRACTICES
  if (isSearch(pathname)) return UI_LOCATION.SEARCH
  if (isEventsPage(pathname)) return UI_LOCATION.EVENTS
  if (isAmaEventsPage(pathname)) return UI_LOCATION.EVENTS_AMA
  if (isFilm(pathname)) return UI_LOCATION.FILMS_DOCS
  if (isRecipe(pathname)) return UI_LOCATION.RECIPES
  if (isSeries(pathname)) return UI_LOCATION.SERIES
  if (isPerson(pathname)) return UI_LOCATION.PERSON
  if (isVideo(pathname)) return UI_LOCATION.VIDEO_PLAYER

  return UI_LOCATION.UNKNOWN_PAGE
}

export function getConfirmationFinishUrl (onboardingCompleted = false, isAiOnly = false) {
  let url = URL_HOME

  // if onboarding is not completed, redirect to get started
  if (onboardingCompleted !== true) {
    url = URL_GET_STARTED
  }

  // if the plan is ai only, redirect to ai chat
  if (isAiOnly) {
    url = URL_AI_CHAT
  }

  return url
}

/**
 * Get the SEO data for paths we cannot look up in other data.
 * args: { pathname, loggedIn }
 */
export function getSeo (args) {
  const pathname = _first(_split(_get(args, 'pathname', ''), '?'))
  const loggedIn = _get(args, 'loggedIn', false)

  if (isMemberHome(pathname, loggedIn)) {
    return {
      title: 'My Gaia Home',
      description: '',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isAnonymousHome(pathname, loggedIn)) {
    return {
      title: 'Gaia - Conscious Media, Yoga & More',
      description:
        'Join the Gaia community today to start streaming thousands of consciousness expanding, yoga and transformational videos to all of your favorite devices.',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isLivePage(pathname)) {
    return {
      title: 'Live',
      ogTitle: 'Live',
      twitterTitle: 'Live',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isLiveChannelPage(pathname)) {
    return {
      title: 'GaiaStream',
      ogTitle: 'GaiaStream',
      twitterTitle: 'GaiaStream',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isEventsPage(pathname)) {
    return {
      title: 'Gaia+ Events',
      description:
        'Get A Year Of Transformation and Global Community. Multi-day Events Streamed Worldwide - Live From Gaia HQ!',
      ogDescription:
        'Get A Year Of Transformation and Global Community. Multi-day Events Streamed Worldwide - Live From Gaia HQ!',
      ogTitle: 'Gaia+ Membership',
      ogImage: getUrl({
        url: '/v1/image-render/5ab611e5-29d5-428f-8b5e-c21ad41a2a21/test.jpg',
        clientType: TYPE_BROOKLYN,
      }),
      twitterCard: 'Summary',
      twitterImage: getUrl({
        url: '/v1/image-render/5ab611e5-29d5-428f-8b5e-c21ad41a2a21/test.jpg',
        clientType: TYPE_BROOKLYN,
      }),
      twitterTitle: 'Gaia+ Membership',
      twitterDescription:
        'Get A Year Of #Transformation and Global #Community with “Gaia+” from @YourMotherGaia.',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isAmaEventsPage(pathname)) {
    return {
      title: 'Gaia+ AMA Events',
      description:
        'Gaia’s Ask Me Anything series is a live, interactive forum for big questions, bold ideas, and real-time exploration.',
      ogDescription:
        'Gaia’s Ask Me Anything series is a live, interactive forum for big questions, bold ideas, and real-time exploration.',
      ogTitle: 'Gaia+ AMA Events',
      noFollow: false,
      noIndex: false,
    }
  }
  if (
    pathname === `${URL_GAIA_PLUS}/caroline-myss-revolutionizing-spirituality`
  ) {
    return {
      title: 'Caroline Myss - Revolutionizing Spirituality',
      description: '',
      noFollow: false,
      noIndex: false,
      ogTitle: 'Caroline Myss - Revolutionizing Spirituality',
    }
  }
  if (pathname === `${URL_GAIA_PLUS}/gregg-braden-human-by-design`) {
    return {
      title: 'Gregg Braden - Human by Design',
      description: '',
      noFollow: false,
      noIndex: false,
      ogDescription:
        'Special Event: Gregg Braden’s “Human By Design” LIVE June 14-16 @ Gaia Headquarters. Limited Seating. Book Now!',
      ogTitle: 'Gregg Braden - Human by Design',
      ogImage: getUrl({
        url: '/v1/image-render/dac28074-cc9d-41b8-8ae9-05b0b42269d5/test.jpg',
        clientType: TYPE_BROOKLYN,
      }),
    }
  }
  if (isEventsDetailsPage(pathname)) {
    return {
      noFollow: false,
      noIndex: false,
    }
  }
  if (isGetStarted(pathname)) {
    return {
      title: 'Get Started',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isPasswordReset(pathname)) {
    return {
      title: 'Reset Password',
      description: '',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isSearch(pathname)) {
    return {
      title: 'Search',
      description: '',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isPlaylist(pathname)) {
    return {
      title: 'My Playlist',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isActivate(pathname)) {
    return {
      title: 'Activate Your Device',
      description: 'Activate your TV device with a code',
      noFollow: true,
      noIndex: false,
    }
  }
  if (isReferJoin(pathname)) {
    return {
      title: 'Plans and Pricing | Gaia Subscription Options',
      description: 'Explore the different subscription plans for Gaia. See pricing and features, and join today to access our collection of mind-expanding videos.',
      ogDescription:
        'Explore conscious living with over 8,000 documentaries, original shows, practices, and films on Gaia.',
      ogTitle: 'Join Gaia Today',
      ogImage:
        'https://gaia-marketing.s3-us-west-2.amazonaws.com/webapp-assets/IAFmetaimage.png',
      twitterCard: 'summary_large_image',
      twitterTitle: 'Join Gaia Today',
      twitterDescription:
        'Explore conscious living with over 8,000 documentaries, original shows, practices, and films on Gaia.',
      twitterImage:
        'https://gaia-marketing.s3-us-west-2.amazonaws.com/webapp-assets/IAFmetaimage.png',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isRefer(pathname)) {
    return {
      title: 'Invite a Friend',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isPolicy(pathname)) {
    return {
      title: 'Terms of Use, Privacy, Policies',
      description: '',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isArticles(pathname)) {
    return {
      title: 'Articles',
      description: '',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isGo(pathname)) {
    return {
      title: 'Go',
      description: '',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isNotFound(pathname)) {
    return {
      title: 'Not Found',
      description: 'The page you were looking for could not be found.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isLogin(pathname)) {
    return {
      title: 'Login',
      description: 'Login to gaia to view your videos.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isLogout(pathname)) {
    return {
      title: 'Logout',
      description: '',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isPlans(pathname)) {
    return {
      title: 'Choose a Gaia Subscription Plan',
      description:
        'Choose from three great Gaia subscription plans and fuel your conscious journey. Become a member today!',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isPlanSelectionPlans(pathname)) {
    return {
      title: 'Select Plan',
      description: 'Select a Gaia plan that is right for you.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isCartCreateAccount(pathname)) {
    return {
      title: 'Create Account | Cart',
      description: 'Set up your Gaia membership.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isCartAccountCreationCreate(pathname)) {
    return {
      title: 'Create Your Account | Cart',
      description: 'Create your gaia account.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isCartBilling(pathname)) {
    return {
      title: 'Billing | Cart',
      description: 'Add checkout billing information.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isCartBillingPayment(pathname)) {
    return {
      title: 'Payment | Cart',
      description: 'Add checkout billing information.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isCartConfirmation(pathname)) {
    return {
      title: 'Confirmation | Cart',
      description: '',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isCartAccessDenied(pathname)) {
    return {
      title: 'Access Denied | Cart',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isMyAccountPage(pathname)) {
    return {
      title: 'My Account',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isFreeMonth(pathname)) {
    return {
      title: 'Free Month',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountProfile(pathname)) {
    return {
      title: 'My Profile',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isMyAccountContactUsPage(pathname)) {
    return {
      title: 'My Account',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountCancelOfferPage(pathname)) {
    return {
      title: 'Cancel Subscription - Offer',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountChangePlan(pathname)) {
    return {
      title: 'Change Plan',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountUpdatePayment(pathname)) {
    return {
      title: 'Update Payment Method',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountSettings(pathname)) {
    return {
      title: 'Account Settings',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountCancelConfirmPage(pathname)) {
    return {
      title: 'Cancel Subscription - Confirmation Page',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountCancel(pathname)) {
    return {
      title: 'Account Cancel',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAccountInvoice(pathname)) {
    return {
      title: 'Subscription Invoice',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isEmailSettings(pathname)) {
    return {
      title: 'Email Settings',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isGiftRedeemPage(pathname)) {
    return {
      title: 'Redeem the Gift of Gaia',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isGiftGiverPage(pathname)) {
    return {
      title: 'Give the Gift of Gaia',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isRecentlyAdded(pathname)) {
    return {
      title: 'New in Gaia',
      description: 'Recently added videos on Gaia.',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isWatchHistory(pathname)) {
    return {
      title: 'My Watch History',
      description: 'User watched videos on Gaia.',
      noFollow: false,
      noIndex: true,
    }
  }
  if (isYogaTeachersPage(pathname)) {
    return {
      title: 'Yoga Teachers on Gaia',
      description:
        'Looking for personal yoga instruction in the comfort of your own home? Then choose from one of our excellent yoga teachers today!',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isYogaPage(pathname)) {
    return {
      title: 'Streaming Online Yoga Videos',
      description:
        'Gaia is known for our immense collection of online yoga videos, which offer you the opportunity to do your yoga workouts on your own schedule. Live your yoga!',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isTopics(pathname)) {
    return {
      title: 'Explore Conscious Expanding Topics Today',
      description:
        'Gaia has the largest library of conscious expanding videos on the web! Frome esoteric spiritual practices to forbidden sciences, Gaia has it all!',
      noFollow: false,
      noIndex: false,
    }
  }
  if (isMultiplePlaylistsPage(pathname)) {
    return {
      title: 'My Playlists',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isAmbassadorsPage(pathname)) {
    return {
      title: 'Ambassador Details',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  if (isOraclePage(pathname)) {
    return {
      title: 'AI Chat Conversation View',
      description: '',
      noFollow: true,
      noIndex: true,
    }
  }
  return {
    title: 'Streaming Films, Original Interviews, Yoga &amp; Fitness',
    description: '',
    noFollow: false,
    noIndex: true,
  }
}
